/**
 * Scalable Theme Controller for Jewellery Guru React App
 * Manages theme switching without affecting existing styles
 * Supports dynamic theme addition and navbar integration
 */

const DEFAULT_PRIMARY_COLOR = "#000000";
/** Old built-in blues — migrate to black so UI matches the new default. */
const LEGACY_PRIMARY_COLORS = new Set([
  "#3b82f6",
  "#2563eb",
  "#2d4a87",
  "#1d4ed8",
]);

class JewelleryGuruThemeController {
  constructor() {
    this.primaryColorStorageKey = "jewellery_guru_primary_color";
    this.defaultPrimaryColor = DEFAULT_PRIMARY_COLOR;
    // Define available themes with metadata
    this.themes = {
      modern: {
        name: "🏢 Modern",
        description: "Clean and contemporary design",
        category: "Professional",
      },
      vintage: {
        name: "📊 Vintage",
        description: "Classic accounting software inspired",
        category: "Traditional",
      },
      premium: {
        name: "✦ Premium",
        description: "Liquid Glass — iOS controls, macOS dock",
        category: "Liquid Glass",
      },
    };

    // Initialize theme system
    this.currentTheme = this.loadSavedTheme();
    this.initialized = false;
    this.callbacks = [];

    // Auto-initialize
    this.init();
  }

  /**
   * Initialize the theme system
   */
  init() {
    if (this.initialized) return;

    this.applyTheme(this.currentTheme);
    this.setupEventListeners();
    this.initialized = true;

  }

  /**
   * Load saved theme from localStorage with fallback
   */
  loadSavedTheme() {
    try {
      return localStorage.getItem("jewellery_guru_theme") || "modern";
    } catch (e) {
      return "modern";
    }
  }

  /**
   * Apply a theme to the document
   * @param {string} themeName - Name of the theme to apply
   */
  applyTheme(themeName) {
    if (!this.themes[themeName]) {
      themeName = "modern";
    }

    // Apply theme to document
    document.documentElement.setAttribute("data-theme", themeName);
    document.documentElement.setAttribute(
      "data-nav-mode",
      themeName === "premium" ? "dock" : "sidebar",
    );

    const savedPrimaryColor = this.loadSavedPrimaryColor();
    const isLegacy =
      savedPrimaryColor &&
      LEGACY_PRIMARY_COLORS.has(savedPrimaryColor.toLowerCase());
    const primaryToApply =
      savedPrimaryColor && !isLegacy
        ? savedPrimaryColor
        : this.defaultPrimaryColor;
    // Persist when migrating legacy blues → black, or when first applying default
    this.applyPrimaryColor(primaryToApply, !savedPrimaryColor || isLegacy);

    // Save to localStorage
    try {
      localStorage.setItem("jewellery_guru_theme", themeName);
    } catch (e) {
    }

    this.currentTheme = themeName;

    // Trigger theme change event
    this.triggerThemeChangeEvent(themeName);

    // Execute callbacks
    this.executeCallbacks(themeName);

  }

  /**
   * Load saved primary color from localStorage
   */
  loadSavedPrimaryColor() {
    try {
      const savedColor = localStorage.getItem(this.primaryColorStorageKey);
      return this.isValidHexColor(savedColor) ? savedColor : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Validate hex color format
   * @param {string | null} color
   */
  isValidHexColor(color) {
    return typeof color === "string" && /^#[0-9A-Fa-f]{6}$/.test(color);
  }

  /**
   * Convert hex color to RGB object
   * @param {string} hexColor
   */
  hexToRgb(hexColor) {
    const normalized = hexColor.replace("#", "");
    return {
      r: parseInt(normalized.substring(0, 2), 16),
      g: parseInt(normalized.substring(2, 4), 16),
      b: parseInt(normalized.substring(4, 6), 16),
    };
  }

  /**
   * Create a lighter color by blending with white
   * @param {string} hexColor
   * @param {number} intensity 0-1
   */
  createLighterColor(hexColor, intensity = 0.85) {
    const { r, g, b } = this.hexToRgb(hexColor);
    const blend = (channel) =>
      Math.round(channel + (255 - channel) * intensity)
        .toString(16)
        .padStart(2, "0");

    return `#${blend(r)}${blend(g)}${blend(b)}`;
  }

  /**
   * Apply a primary color override globally
   * @param {string} primaryColor
   * @param {boolean} persist
   */
  applyPrimaryColor(primaryColor, persist = true) {
    if (!this.isValidHexColor(primaryColor)) {
      return;
    }

    const root = document.documentElement;
    const { r, g, b } = this.hexToRgb(primaryColor);
    const primaryLight = this.createLighterColor(primaryColor);

    root.style.setProperty("--theme-primary", primaryColor);
    root.style.setProperty("--theme-primary-light", primaryLight);
    root.style.setProperty("--theme-info", primaryColor);
    root.style.setProperty("--theme-navbar-bg", primaryColor);
    root.style.setProperty("--vintage-header-bg", primaryColor);
    root.style.setProperty("--bs-primary", primaryColor);
    root.style.setProperty("--bs-primary-rgb", `${r}, ${g}, ${b}`);
    root.style.setProperty("--bs-link-color", primaryColor);
    root.style.setProperty(
      "--theme-primary-shadow",
      `0 17px 20px -8px rgba(${r}, ${g}, ${b}, 0.28)`,
    );
    root.style.setProperty(
      "--theme-primary-shadow-sm",
      `0 2px 6px rgba(${r}, ${g}, ${b}, 0.22)`,
    );

    if (persist) {
      try {
        localStorage.setItem(this.primaryColorStorageKey, primaryColor);
      } catch (e) {
        console.warn("Could not save primary color preference:", e);
      }
    }
  }

  /**
   * Get current effective primary color
   */
  getCurrentPrimaryColor() {
    const computedColor = getComputedStyle(document.documentElement)
      .getPropertyValue("--theme-primary")
      .trim();

    if (this.isValidHexColor(computedColor)) {
      return computedColor;
    }

    return this.loadSavedPrimaryColor() || this.defaultPrimaryColor;
  }

  /**
   * Setup global event listeners
   */
  setupEventListeners() {
    // Listen for manual theme attribute changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-theme"
        ) {
          const newTheme = document.documentElement.getAttribute("data-theme");
          if (newTheme && newTheme !== this.currentTheme) {
            this.currentTheme = newTheme;
          }
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
  }

  /**
   * Trigger custom theme change event
   * @param {string} themeName - Name of the new theme
   */
  triggerThemeChangeEvent(themeName) {
    const event = new CustomEvent("themeChanged", {
      detail: {
        theme: themeName,
        themeData: this.themes[themeName],
        previousTheme: this.currentTheme,
      },
      bubbles: true,
    });

    document.dispatchEvent(event);
  }

  /**
   * Add callback for theme changes
   * @param {Function} callback - Function to call when theme changes
   */
  onThemeChange(callback) {
    if (typeof callback === "function") {
      this.callbacks.push(callback);
    }
  }

  /**
   * Execute all registered callbacks
   * @param {string} themeName - Current theme name
   */
  executeCallbacks(themeName) {
    this.callbacks.forEach((callback) => {
      try {
        callback(themeName, this.themes[themeName]);
      } catch (e) {
        console.error("Error executing theme callback:", e);
      }
    });
  }

  /**
   * Add a new theme dynamically
   * @param {string} key - Theme key/identifier
   * @param {string} name - Display name
   * @param {string} description - Theme description
   * @param {Object} variables - CSS variables for the theme
   * @param {string} category - Theme category
   */
  addTheme(key, name, description, variables, category = "Custom") {
    // Add to themes registry
    this.themes[key] = {
      name,
      description,
      category,
    };

    // Create CSS for the new theme
    this.injectThemeCSS(key, variables);

  }

  /**
   * Inject CSS variables for a theme
   * @param {string} themeName - Theme name
   * @param {Object} variables - CSS variables object
   */
  injectThemeCSS(themeName, variables) {
    const css = `
[data-theme="${themeName}"] {
${Object.entries(variables)
  .map(([prop, value]) => `  --theme-${prop}: ${value};`)
  .join("\n")}
}`;

    // Create or update style element
    let styleElement = document.getElementById(`theme-${themeName}-vars`);
    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = `theme-${themeName}-vars`;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = css;
  }

  /**
   * Remove a theme
   * @param {string} key - Theme key to remove
   */
  removeTheme(key) {
    if (key === "modern") {
      console.warn("Cannot remove the default modern theme");
      return;
    }

    // Switch to modern if removing current theme
    if (this.currentTheme === key) {
      this.applyTheme("modern");
    }

    // Remove from registry
    delete this.themes[key];

    // Remove CSS
    const styleElement = document.getElementById(`theme-${key}-vars`);
    if (styleElement) {
      styleElement.remove();
    }

  }

  /**
   * Get current theme information
   */
  getCurrentTheme() {
    return {
      key: this.currentTheme,
      ...this.themes[this.currentTheme],
    };
  }

  /**
   * Get all available themes
   */
  getAllThemes() {
    return { ...this.themes };
  }
}

// Initialize Global Theme Controller
let themeController = null;

// Initialize when DOM is ready or immediately if already ready
const initializeThemeController = () => {
  if (!themeController) {
    themeController = new JewelleryGuruThemeController();

    // Expose to window for global access
    window.JGThemeController = themeController;

    // Expose convenient global functions
    window.changeTheme = (theme) => themeController.applyTheme(theme);
    window.changePrimaryColor = (color) =>
      themeController.applyPrimaryColor(color);
    window.getCurrentTheme = () => themeController.getCurrentTheme();
    window.getCurrentPrimaryColor = () =>
      themeController.getCurrentPrimaryColor();
    window.addCustomTheme = (key, name, desc, vars, cat) =>
      themeController.addTheme(key, name, desc, vars, cat);

  }
};

// Initialize immediately
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeThemeController);
} else {
  initializeThemeController();
}

// Export for ES6 modules
export { JewelleryGuruThemeController, initializeThemeController };
export default JewelleryGuruThemeController;
